import { motion, useScroll, useMotionValueEvent } from "motion/react";
import { useState, useRef } from "react";
import logoLockup from "@/assets/images/basilica-logo-lockup.svg";
import { BasilicaLogo } from "@/components/basilica-logo";
import { cn } from "@/lib/utils";

function useScrollDirection(containerRef: React.RefObject<HTMLDivElement | null>) {
	const [hidden, setHidden] = useState(false);
	const { scrollY } = useScroll({ container: containerRef });
	const lastScrollY = useRef(0);

	useMotionValueEvent(scrollY, "change", (latest) => {
		const previous = lastScrollY.current;
		const direction = latest > previous ? "down" : "up";
		if (direction === "down" && latest > 100) {
			setHidden(true);
		} else if (direction === "up") {
			setHidden(false);
		}
		lastScrollY.current = latest;
	});

	return hidden;
}

function Button({ children, onClick, className }: { children: React.ReactNode; onClick?: () => void; className?: string }) {
	return (
		<motion.button
			onClick={onClick}
			className={cn(
				"group relative flex items-center justify-center gap-2.5 rounded-xl",
				"transition-all duration-100 px-4 py-2 h-[40px]",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30",
				"bg-white/10 hover:bg-white/90",
				className
			)}
		>
			<span className="font-sans font-normal text-[14px] transition-colors duration-100 text-white group-hover:text-[#0a0a0a]">
				{children}
			</span>
		</motion.button>
	);
}

export function PageLayout({ children, activeRoute }: { children: React.ReactNode; activeRoute?: string }) {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const headerHidden = useScrollDirection(scrollContainerRef);

	return (
		<div ref={scrollContainerRef} className="relative h-full w-full overflow-y-auto overflow-x-hidden bg-[#0a0a0a] text-white pt-5">
			<motion.header
				initial={{ y: 0 }}
				animate={{ y: headerHidden ? "-150%" : 0 }}
				transition={{ duration: 0.4, ease: [0.7, 0, 0.2, 1] }}
				className="sticky top-0 z-40 px-4 md:px-[8%] lg:px-[15%] py-3"
			>
				<div className="relative bg-[#050505]/80 backdrop-blur-xl rounded-xl overflow-hidden">
					<div
						className="pointer-events-none absolute inset-0 opacity-[0.08]"
						style={{
							backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
						}}
					/>
					<nav className="relative flex items-center justify-between px-5 py-3">
						<motion.a
							href="/"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.5 }}
							className="flex items-center"
						>
							<img src={logoLockup} alt="Basilica" className="h-[22px]" />
						</motion.a>

						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.5, delay: 0.1 }}
							className="flex items-center gap-8"
						>
							<a
								href="/about"
								className={cn(
									"text-[15px] transition-colors",
									activeRoute === "/about" ? "text-white" : "text-white/60 hover:text-white"
								)}
							>
								About Us
							</a>
							<Button onClick={() => {
								window.location.href = "/#contact";
							}}>
								Contact
							</Button>
						</motion.div>
					</nav>
				</div>
			</motion.header>

			{children}

			<footer className="relative mt-16 md:mt-24 pt-8 pb-12 md:pb-20 px-9 md:px-[8%] lg:px-[15%] border-t border-white/5">
				<div className="flex flex-col md:flex-row items-center justify-between gap-6">
					<div className="flex items-center gap-2">
						<span className="text-[15px] text-white/40">© 2026 Basilica Digital Inc.</span>
					</div>
					<div className="flex items-center gap-5">
						<a href="/privacy" className="text-[15px] text-white/40 hover:text-white transition-colors duration-150">Privacy Policy</a>
						<a href="/terms" className="text-[15px] text-white/40 hover:text-white transition-colors duration-150">Terms of Use</a>
					</div>
				</div>
			</footer>
		</div>
	);
}
